import clsx from 'clsx';
import { Form, Formik } from 'formik';
import React, { useRef } from 'react';
import * as Yup from 'yup';

import { PrimaryButton } from '@components/Button/variants';
import SwitchField from '@components/Form/SwitchField';
import TextField from '@components/Form/TextField';
import FormErrors from '@components/FormErrors';
import { useTeamUpdate } from '@hooks';
import useEventTracking from '@hooks/useEventTracking';
import { Theme } from '@interfaces/global';
import { ClickTarget, click } from '@tracking';
import { Team } from '@utils/backend-api/teams';

import SettingHeaderSeperator from '../SettingHeaderSeperator';

const EMAIL_DOMAIN_REGEX = /^((?:https?:\/\/)?[^./]+(?:\.[^./]+)+(?:\/.*)?)$/;

const validationSchema = () =>
  Yup.object({
    emailDomain: Yup.string().matches(EMAIL_DOMAIN_REGEX, 'Please enter a valid domain').nullable(),
  });

export default function EmailDomainForm({ team }: { team: Team }) {
  const { trackEvent } = useEventTracking();
  const { updateTeamAsync, updateTeamData, isTeamUpdateLoading } = useTeamUpdate();
  const skipNextToggle = useRef(false);

  const initialValues = {
    emailDomain: team?.email_domain?.replace('@', '0') || '',
    addByDomain: !!team?.email_domain,
  };

  const handleSubmit = async (values) => {
    await updateTeamAsync({
      email_domain: values.emailDomain,
    });
    trackEvent(click({ clickTarget: ClickTarget.SaveTeamDomain, clickText: values.emailDomain }));
  };

  const handleToggle = async (
    checked: boolean,
    setFieldValue: (field: string, value: unknown) => void
  ) => {
    if (skipNextToggle.current) {
      skipNextToggle.current = false;
      return;
    }

    trackEvent(
      click({
        clickTarget: ClickTarget.Toggle,
        clickText: `Add by domain: ${checked ? 'true' : 'false'}`,
      })
    );

    if (checked) return;

    try {
      const resp = await updateTeamAsync({ email_domain: null });
      if (resp?.success) {
        setFieldValue('emailDomain', '');
      } else {
        skipNextToggle.current = true;
        setFieldValue('addByDomain', true);
      }
    } catch {
      skipNextToggle.current = true;
      setFieldValue('addByDomain', true);
    }
  };

  return (
    <div className="mb-48" data-test="team-settings-domain">
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        enableReinitialize={true}
      >
        {({ isSubmitting, values, setFieldValue }) => (
          <Form>
            <SettingHeaderSeperator sectionName="Add by corporate domain" />
            <div className="flex items-start">
              <div
                className={clsx('font-suisse-intl text-description-lg', {
                  'mb-24': values.addByDomain,
                })}
              >
                Allow anyone on my team who signs in with their corporate email to subscribe to The
                Information on my account. Each additional team member will be charged at an annual
                cost of $399 per year plus tax.
              </div>
              <SwitchField
                colorTheme={Theme.Light}
                name="addByDomain"
                disabled={isTeamUpdateLoading}
                onChangeCallBack={({ checked }) => handleToggle(checked, setFieldValue)}
              />
            </div>
            {values.addByDomain && (
              <>
                <TextField
                  className="mb-24"
                  name="emailDomain"
                  placeholder="Enter a corporate domain (e.g., example.com)"
                />
                {!!updateTeamData?.errors && <FormErrors errors={updateTeamData.errors} />}
                <PrimaryButton
                  as="button"
                  className="mt-24"
                  type="submit"
                  disabled={isSubmitting}
                  fullWidth
                >
                  Save
                </PrimaryButton>
              </>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
}
